/**
 * use-ui-language.ts — Ngôn ngữ giao diện.
 *
 *   GET/PUT /ui-settings/me   ngôn ngữ của tôi (null = chưa chọn)
 *   PUT     /ui-settings/org  ngôn ngữ mặc định của tổ chức (Cài đặt → Thông tin công ty)
 *
 * Thứ tự ưu tiên: lựa chọn đã lưu trên server → lựa chọn trên trình duyệt này
 * (localStorage) → mặc định tổ chức → ngôn ngữ trình duyệt (nếu hỗ trợ) → tiếng Việt.
 * Đổi ngôn ngữ áp dụng ngay, không tải lại trang.
 */
import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api-client'
import { changeLanguage, storedLanguage } from '@/lib/i18n'
import { useAuthStore } from '@/stores/auth-store'
import { findLanguage, SOURCE_LANGUAGE } from '@/locales/languages'

export interface UiSettingsMe {
  language: string | null
  orgDefaultLanguage: string | null
  supported: string[]
}

export const uiSettingsKeys = { me: ['ui-settings', 'me'] as const }

export function useUiSettings(enabled = true) {
  return useQuery({
    queryKey: uiSettingsKeys.me,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get<UiSettingsMe>('/ui-settings/me')).data,
  })
}

/** Ngôn ngữ đang dùng + hàm đổi (lưu localStorage và server nếu đã đăng nhập). */
export function useLanguage() {
  const { i18n } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const save = useMutation({
    mutationFn: async (language: string | null) => (await api.put<{ language: string | null }>('/ui-settings/me', { language })).data,
    onSuccess: (d) => qc.setQueryData<UiSettingsMe>(uiSettingsKeys.me, (old) => (old ? { ...old, language: d.language } : old)),
  })
  const current = findLanguage(i18n.resolvedLanguage ?? i18n.language)?.code ?? SOURCE_LANGUAGE

  const setLanguage = async (code: string) => {
    await changeLanguage(code)
    // Phiên "xem dưới quyền" không ghi đè tuỳ chọn của nhân viên đó.
    if (user && !user.impersonatedBy) save.mutate(code)
  }
  return { current, setLanguage, saving: save.isPending }
}

/**
 * Đồng bộ ngôn ngữ sau khi đăng nhập (gắn một lần trong khung ứng dụng):
 * server có lựa chọn → dùng; chưa có và trình duyệt cũng chưa chọn → mặc định tổ chức.
 */
export function useLanguageSync() {
  const user = useAuthStore((s) => s.user)
  const { data } = useUiSettings(!!user)
  const applied = useRef<string | null>(null)
  useEffect(() => {
    if (!data) return
    const target = data.language ?? (storedLanguage() ? null : data.orgDefaultLanguage)
    if (!target || applied.current === target) return
    applied.current = target
    // Lựa chọn của server được nhớ lại trên máy; mặc định tổ chức thì không (để
    // tổ chức đổi mặc định sau này vẫn có hiệu lực).
    void changeLanguage(target, !!data.language)
  }, [data])
}

export function useSaveOrgLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (defaultLanguage: string | null) =>
      (await api.put<{ defaultLanguage: string | null }>('/ui-settings/org', { defaultLanguage })).data,
    onSuccess: (d) =>
      qc.setQueryData<UiSettingsMe>(uiSettingsKeys.me, (old) => (old ? { ...old, orgDefaultLanguage: d.defaultLanguage } : old)),
  })
}
